import React, { useEffect, useState } from 'react';
import { Link } from "react-router-dom";
import { Helmet } from 'react-helmet';
import { init, send } from 'emailjs-com';
import Navbar from './Navbar';
import Footer from './Footer';
import { TbTemperature, TbBatteryCharging, TbTrendingDown, TbShieldCheck, TbChartLine, TbBuildingWarehouse, TbPlug, TbMapPin } from 'react-icons/tb';

init("d1zH6UNPIGfFtdCEu");

function ColdStorageSolar() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    companyName: '',
    facilityType: '',
    message: ''
  });

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // Update document title and meta tags for SEO
    document.title = "Solar for Cold Storage | Cold Chain Solar Solutions | Lunar Electric";

    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Solar-powered cold storage solutions designed to reduce energy cost per pallet and protect 24×7 temperature integrity. Optimize grid + solar mix for your multi-chamber cold warehouse.');
    }

    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'solar for cold storage, cold chain solar, temperature controlled logistics solar, cold warehouse solar, CFC solar, multi-chamber cold storage solar, cold chain energy efficiency, solar cold storage India, kWh per MT, energy cost per pallet');
    }

    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', 'Solar for Cold Storage | Cold Chain Solar Solutions | Lunar Electric');
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', 'Solar-powered cold storage that cuts your energy cost per pallet and protects 24×7 temperature integrity.');
    }

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', 'https://lunarelectric.in/solar-cold-storage');
    }

    // Update canonical URL
    let linkCanonical = document.querySelector('link[rel="canonical"]');
    if (!linkCanonical) {
      linkCanonical = document.createElement('link');
      linkCanonical.setAttribute('rel', 'canonical');
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.setAttribute('href', 'https://lunarelectric.in/solar-cold-storage');
  }, []);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const templateParams = {
      to_name: 'Lunar Electric',
      from_name: formData.name,
      from_email: formData.email,
      phone: formData.phone,
      company_name: formData.companyName,
      facility_type: formData.facilityType,
      message: `Cold Storage Inquiry: ${formData.message}`,
      subject: 'Cold Storage Solar Inquiry'
    };

    send('service_8x7kqhj', 'template_cold_storage', templateParams)
      .then((response) => {
        alert('Thank you! We will contact you soon.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          companyName: '',
          facilityType: '',
          message: ''
        });
      })
      .catch((error) => {
        alert('Failed to send inquiry, please try again.');
        console.error('EmailJS error:', error);
      });
  };

  return (
    <>
      <Helmet>
        <title>Solar for Cold Storage | Cold Chain Solar Solutions | Lunar Electric</title>
        <meta name="description" content="Solar-powered cold storage solutions designed to reduce energy cost per pallet and protect 24×7 temperature integrity. Optimize grid + solar mix for your multi-chamber cold warehouse." />
        <meta name="keywords" content="solar for cold storage, cold chain solar, temperature controlled logistics solar, cold warehouse solar, CFC solar, multi-chamber cold storage solar, cold chain energy efficiency, solar cold storage India" />
        <link rel="canonical" href="https://lunarelectric.in/solar-cold-storage" />
      </Helmet>
      <Navbar />
      <div className="cold-storage-page">
        {/* Hero Section */}
        <section className="cold-storage-hero">
          <div className="container">
            <div className="row">
              <div className="col-md-12 text-center">
                <h1 className="hero-title">
                  Solar-Powered Cold Storage That Cuts Your Energy Cost Per Pallet
                </h1>
                <p className="hero-subtitle">
                  Reduce kWh/MT and diesel hours while maintaining 24×7 temperature integrity across all chambers
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Why We Specialize Section */}
        <section className="specialization-section">
          <div className="container">
            <div className="row">
              <div className="col-md-12">
                <h2 className="section-title">Why We Specialize in Cold Chain Solar Solutions</h2>
                <p className="section-intro">
                  We design solar solutions specifically for multi-chamber cold warehouses, integrated cold chains, CFS (Container Freight Stations), 
                  and ICDs (Inland Container Depots). Our cold chain expertise means we understand your critical metrics—kWh/MT, energy cost per pallet, 
                  and temperature integrity requirements.
                </p>
                
                <div className="specialization-grid">
                  <div className="specialization-card">
                    <TbTemperature className="specialization-icon" />
                    <h3>Cold Chain Expertise</h3>
                    <p>We understand temperature-controlled logistics, multi-chamber cold warehouses, CFS (Container Freight Stations), ICD (Inland Container Depots), and G+4/G+5 racking facilities. Our designs account for ante room and blast freezer loads, ensuring temperature integrity is never compromised.</p>
                  </div>
                  <div className="specialization-card">
                    <TbChartLine className="specialization-icon" />
                    <h3>Load-Curve Engineering</h3>
                    <p>We build solar plants around your compressor and auxiliary load profile, not generic thumb rules. Our design considers connected load of refrigeration compressors, auxiliaries, and critical plug loads (reefer plug-ins, dock equipment).</p>
                  </div>
                  <div className="specialization-card">
                    <TbBatteryCharging className="specialization-icon" />
                    <h3>Grid + Solar + DG Integration</h3>
                    <p>Our integrated architecture optimizes grid + rooftop solar + DG systems to maximize solar contribution during peak tariff and high-outage seasons, cutting DG usage while maintaining 24×7 uptime.</p>
                  </div>
                  <div className="specialization-card">
                    <TbShieldCheck className="specialization-icon" />
                    <h3>Compliance & Standards</h3>
                    <p>We ensure your solar solution supports energy efficiency and cooling targets under national actions like ICAP and emerging cold chain standards, keeping you ahead of regulatory requirements.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Key Metrics That Matter Section */}
        <section className="metrics-section">
          <div className="container">
            <div className="row">
              <div className="col-md-12">
                <h2 className="section-title">Metrics That Matter to Your Operations</h2>
                <p className="section-intro">
                  We track and optimize the KPIs that directly impact your cold chain operations and profitability. 
                  Our solutions are designed to improve these critical metrics:
                </p>
                
                <div className="metrics-grid">
                  <div className="metric-card">
                    <div className="metric-header">
                      <TbTrendingDown className="metric-icon" />
                      <h3>Energy Cost Per Pallet</h3>
                    </div>
                    <p className="metric-description">
                      Designed to reduce energy cost per pallet by optimising grid + solar mix for your actual load curve. 
                      This directly impacts your EBITDA margin and pallet economics.
                    </p>
                    <div className="metric-impact">
                      <strong>Impact:</strong> Stabilise EBITDA per pallet by de-risking 15–20% of your power cost from tariff hikes and outages
                    </div>
                  </div>

                  <div className="metric-card">
                    <div className="metric-header">
                      <TbChartLine className="metric-icon" />
                      <h3>Specific Energy Consumption (SEC)</h3>
                    </div>
                    <p className="metric-description">
                      Target lower kWh/MT (kWh per metric ton) or kWh/m³/yr and improve your Specific Energy Consumption 
                      without compromising chamber temperature. Used as benchmarks for cold warehouses.
                    </p>
                    <div className="metric-impact">
                      <strong>Impact:</strong> Improve energy performance benchmarks per commodity, aligning with ICAP standards
                    </div>
                  </div>

                  <div className="metric-card">
                    <div className="metric-header">
                      <TbBatteryCharging className="metric-icon" />
                      <h3>Grid vs Diesel Split</h3>
                    </div>
                    <p className="metric-description">
                      Track and optimize units from grid vs units from DG and diesel hours per month as reliability 
                      and cost KPIs. Cut diesel generator run-hours per month while maintaining 24×7 temperature control.
                    </p>
                    <div className="metric-impact">
                      <strong>Impact:</strong> Reduce operational costs and improve reliability metrics
                    </div>
                  </div>

                  <div className="metric-card">
                    <div className="metric-header">
                      <TbPlug className="metric-icon" />
                      <h3>Contract Demand & Peak kVA</h3>
                    </div>
                    <p className="metric-description">
                      Demand charges and penalties matter significantly for multi-chamber high-rack facilities. 
                      Our solutions help optimize contract demand and reduce peak kVA charges.
                    </p>
                    <div className="metric-impact">
                      <strong>Impact:</strong> Lower demand charges and penalties, improving overall power cost structure
                    </div>
                  </div>

                  <div className="metric-card">
                    <div className="metric-header">
                      <TbTemperature className="metric-icon" />
                      <h3>Uptime at Set Temperature</h3>
                    </div>
                    <p className="metric-description">
                      Maintain hours within set-point band or minimize temperature excursions per year, 
                      which relate directly to product safety and customer SLAs.
                    </p>
                    <div className="metric-impact">
                      <strong>Impact:</strong> Protect temperature integrity during grid outages, reducing product loss risk
                    </div>
                  </div>

                  <div className="metric-card">
                    <div className="metric-header">
                      <TbBuildingWarehouse className="metric-icon" />
                      <h3>Solar Share of Annual Units</h3>
                    </div>
                    <p className="metric-description">
                      Achieve significant solar share of annual units without disturbing day-to-day operations or FIFO systems. 
                      Maximize solar contribution during peak tariff periods.
                    </p>
                    <div className="metric-impact">
                      <strong>Impact:</strong> Increase renewable energy share while maintaining operational efficiency
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Value Propositions */}
        <section className="value-props-section">
          <div className="container">
            <div className="row">
              <div className="col-md-12">
                <h2 className="section-title">How We Deliver Results</h2>
                <div className="value-props-grid">
                  <div className="value-prop-card">
                    <h3>Load-Curve Based Design</h3>
                    <p>We build the solar plant around your compressor and auxiliary load profile, not generic thumb rules. Our design considers your connected load of refrigeration compressors, auxiliaries, and critical plug loads (reefer plug-ins, dock equipment).</p>
                  </div>
                  <div className="value-prop-card">
                    <h3>Peak Tariff & DG Relief</h3>
                    <p>Target maximum solar contribution in peak tariff and high-outage seasons to cut DG usage. This helps optimize your contract demand and reduce demand charges and penalties for multi-chamber high-rack facilities.</p>
                  </div>
                  <div className="value-prop-card">
                    <h3>Multi-Site Ready</h3>
                    <p>Pan-India deployment for networks like yours, including CFS and ICD facilities, with central monitoring of plant kWh, diesel hours, and chamber uptime. Ideal for integrated cold chain operations with G+4/G+5 racking facilities.</p>
                  </div>
                  <div className="value-prop-card">
                    <h3>Solar-Ready Expansion</h3>
                    <p>Rooftop solar on G+4/G+5 racking sheds with solar-ready expansion for new chambers. Designed to not disturb day-to-day operations or FIFO systems while maximizing roof space utilization.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Industry Benefits */}
        <section className="benefits-section">
          <div className="container">
            <div className="row">
              <div className="col-md-12">
                <h2 className="section-title">Benefits for Your Cold Chain Operations</h2>
                <div className="benefits-list">
                  <div className="benefit-item">
                    <h4>Reduce Product Loss Risk</h4>
                    <p>Protect temperature integrity during grid outages, reducing product loss risk from power cuts and ensuring compliance with customer SLAs. Maintain uptime at set temperature across all chambers.</p>
                  </div>
                  <div className="benefit-item">
                    <h4>Improve Energy Performance Benchmarks</h4>
                    <p>Lower your Specific Energy Consumption (SEC) in kWh/MT and improve energy performance benchmarks per commodity, aligning with ICAP standards for energy efficient cold chain operations.</p>
                  </div>
                  <div className="benefit-item">
                    <h4>Decarbonise Cold Chain Operations</h4>
                    <p>Reduce your carbon footprint while maintaining operational efficiency, supporting your ESG goals and sustainability initiatives. Contribute to decarbonising cold chain operations.</p>
                  </div>
                  <div className="benefit-item">
                    <h4>Stabilise EBITDA Per Pallet</h4>
                    <p>De-risk power costs from tariff hikes and outages, helping stabilise your EBITDA per pallet and improve overall profitability. Electricity cost per pallet per month is directly linked to EBITDA margin.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form Section */}
        <section className="contact-form-section">
          <div className="container">
            <div className="row">
              <div className="col-lg-5 col-md-6">
                <div className="form-side-content">
                  <h3>Why Choose Lunar Electric?</h3>
                  <div className="stats-grid">
                    <div className="stat-item">
                      <div className="stat-number">15-20%</div>
                      <div className="stat-label">Power Cost Reduction</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-number">24×7</div>
                      <div className="stat-label">Temperature Integrity</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-number">Pan-India</div>
                      <div className="stat-label">Deployment Network</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-number">ICAP</div>
                      <div className="stat-label">Compliance Ready</div>
                    </div>
                  </div>
                  <div className="key-benefits">
                    <h4>Key Benefits:</h4>
                    <ul>
                      <li>Reduce energy cost per pallet</li>
                      <li>Lower kWh/MT consumption</li>
                      <li>Cut diesel generator hours</li>
                      <li>Optimize contract demand</li>
                      <li>Maintain temperature integrity</li>
                      <li>Support CFS & ICD operations</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="col-lg-6 col-md-6 offset-lg-1">
                <div className="form-wrapper">
                  <h2 className="form-title">Get a Customized Solar Solution</h2>
                  <p className="form-subtitle">
                    Tell us about your facility and we'll design a solution that optimizes your energy cost per pallet.
                  </p>
                  
                  <form onSubmit={handleSubmit} className="cold-storage-form">
                    <div className="form-group">
                      <label htmlFor="name">Full Name *</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="form-input"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="email">Email Address *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="form-input"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="phone">Phone Number *</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="form-input"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="companyName">Company/Facility Name *</label>
                      <input
                        type="text"
                        id="companyName"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleInputChange}
                        className="form-input"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="facilityType">Facility Type *</label>
                      <select
                        id="facilityType"
                        name="facilityType"
                        value={formData.facilityType}
                        onChange={handleInputChange}
                        className="form-input"
                        required
                      >
                        <option value="">Select Facility Type</option>
                        <option value="Multi-chamber Cold Warehouse">Multi-chamber Cold Warehouse</option>
                        <option value="CFS (Container Freight Station)">CFS (Container Freight Station)</option>
                        <option value="ICD (Inland Container Depot)">ICD (Inland Container Depot)</option>
                        <option value="CFC (Controlled Atmosphere Facility)">CFC (Controlled Atmosphere Facility)</option>
                        <option value="Integrated Cold Chain">Integrated Cold Chain</option>
                        <option value="Temperature-Controlled Logistics">Temperature-Controlled Logistics</option>
                        <option value="Blast Freezer Facility">Blast Freezer Facility</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="message">Tell us about your facility (chambers, capacity, current energy costs, etc.)</label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        className="form-input"
                        rows="5"
                        placeholder="e.g., Number of chambers, total capacity in MT, current kWh/MT, diesel hours per month, contract demand..."
                      />
                    </div>

                    <div className="form-submit">
                      <button type="submit" className="btn btn-primary">
                        Get Free Consultation
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="cta-section">
          <div className="container">
            <div className="row">
              <div className="col-md-12 text-center">
                <h2>Ready to Optimize Your Cold Storage Energy Costs?</h2>
                <p>De-risk your cold chain from grid failures and diesel price shocks with tailored solar for temperature-controlled logistics</p>
                <Link to="/" className="btn btn-secondary">
                  ← Back to Home
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
}

export default ColdStorageSolar;
